import React, { useMemo } from 'react'

import {
  NewPatientFormState,
  useStates,
  usePronouns,
  useGenderIdentities,
  usePreferredLanguages,
  SignupType,
} from './utils'
import { secureCache } from '~/utils/secureCache'
import { useHistory } from 'react-router-dom'
import { logEvent } from '~/utils/events'
import {
  DemographicsCollectionFormUI,
  DemographicsCollectionFormValues,
  getDefaultPreferredLanguage,
} from '~/components/screens/DemographicsCollectionFormUI'

const DemographicCollectionForm: React.FC = () => {
  const history = useHistory()
  const states = useStates()
  const preferredLanguages = usePreferredLanguages()
  const pronouns = usePronouns()
  const genderIdentities = useGenderIdentities()
  const cachedPayerId = secureCache.get('insurancePayerId')

  const isLoading =
    pronouns.isLoading ||
    states.isLoading ||
    preferredLanguages.isLoading ||
    genderIdentities.isLoading

  const defaultPreferredLanguage = useMemo(
    () => getDefaultPreferredLanguage(preferredLanguages.data),
    [preferredLanguages.data],
  )

  if (!secureCache.get('firstName')) history.push({ pathname: '/signup/name' })

  const initialState: NewPatientFormState = {
    firstName: secureCache.get('firstName') || '',
    lastName: secureCache.get('lastName') || '',
    preferredName: secureCache.get('preferredName') || '',
    termsAndConditions: (secureCache.get('termsAndConditions') === 'true') as boolean,
    consentForms: JSON.parse(secureCache.get('consentForms') || '[]') as number[],
    dob: secureCache.get('dob') || '',
    insurancePayerId: cachedPayerId ? Number(cachedPayerId) : null,
    pronouns: JSON.parse(secureCache.get('pronouns') || 'null'),
    gender: JSON.parse(secureCache.get('gender') || 'null'),
    sex: JSON.parse(secureCache.get('sex') || 'null'),
    state: JSON.parse(secureCache.get('state') || 'null'),
    preferredLanguage: JSON.parse(
      secureCache.get('preferredLanguage') || JSON.stringify(defaultPreferredLanguage),
    ),
    preferredLanguageOther: secureCache.get('preferredLanguageOther') || '',
  }

  const handleSubmit = async (values: DemographicsCollectionFormValues) => {
    for (const value in values) {
      if (typeof values[value] === 'string') secureCache.set(value, values[value])
      else secureCache.set(value, JSON.stringify(values[value]))
    }

    // Ineligible state check for CARE_ONLY_MEMBER flow
    const signupType = secureCache.get('signupType') as SignupType
    const isCareMember = signupType === SignupType.CARE_ONLY_MEMBER
    if (values.state?.canService === false && isCareMember) {
      history.push({ pathname: '/signup/cannot-service', state: { states: values.state } })
      return
    }

    logEvent('DEMOGRAPHIC_SCREEN_COMPLETED', {})
    history.push('/signup/create-account')
  }

  return (
    <DemographicsCollectionFormUI
      initialValues={initialState}
      onSubmit={handleSubmit}
      isLoading={isLoading}
      submitting={false}
      states={states.data || []}
      pronouns={pronouns.data || []}
      genderIdentities={genderIdentities.data || []}
      preferredLanguages={preferredLanguages.data}
    />
  )
}
export default DemographicCollectionForm
